import React, { useState, useTransition, lazy, Suspense } from 'react'
import { Card, Button, Col, List, Row } from 'antd'
import AliceCarousel from 'react-alice-carousel'
import ModalVideo from 'react-modal-video'
import { HeaderHero, SEO } from './components'
import { NEWS_LIST, MEDIA_VIDEO_URLS, RESPONSIVE_VIDEO_BREAK_POINTS, MORE_VIDEOS, EXTRA_VIDEO_URLS } from '../constants'

import NewsMediaStyle from '../../styles/NewsMedia.module.css'
import ModalVideoStyle from '../../styles/ModalVideo.module.css'
import PlayerStyle from './Player/Player.module.css'
import 'react-alice-carousel/lib/alice-carousel.css'

const tabletImage = 'hero/news-media/News-Media_1024x300-no-copy.jpg'
const desktopImage = 'hero/news-media/News-Media_1440x300-no-copy.jpg'
const largeScreenImage = 'hero/news-media/News-Media_1620x300-no-copy.jpg'
const xtraLargeScreenImage = 'hero/news-media/News-Media_1960x300-no-copy.jpg'
const smallImage = 'hero/news-media/News-Media_375x188.jpg'
const smallImage390 = 'hero/news-media/News-Media_390x188.jpg'
const mobileImage = 'hero/news-media/News-Media_428x188.jpg'
const mobileImage768 = 'hero/news-media/News-Media_768x300-no-copy.jpg'

const LIMIT = 4
const Player = lazy(() => import("./Player/Player.js"));

const NewsMedia = () => {
  const [list, setList] = useState([...NEWS_LIST].slice(0, 4))
  const [isVideoOpen, setVideoOpen] = useState(false)
  const [currentVideoId, setCurrentVideoId] = useState('')
  const [index, setIndex] = useState(LIMIT)
  const [showLess, setShowLess] = useState(false)
  const [showMore, setShowMore] = useState(true)
  const [isLoadMoreDataLoading, setLoadMoreDataLoading] = useState(false)
  const [isLoadLessDataLoading, setLoadLessDataLoading] = useState(false)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [, startTransition] = useTransition();
  const [hasLoaded, setHasLoaded] = useState(false);

  const sleep = milliseconds => {
    return new Promise(resolve => setTimeout(resolve, milliseconds))
  }

  const onLoadMore = async () => {
    const clonedList = [...list]
    const newIndex = index + LIMIT
    const newList = [...NEWS_LIST].slice(index, newIndex)
    const updatedList = clonedList.concat(newList)
    setLoadMoreDataLoading(true)
    await sleep(1000)
    setIndex(newIndex)
    setList(updatedList)
    const lessButton = updatedList.length > 4 && updatedList.length > 0
    setShowLess(lessButton)
    const showMoreBtn = [...NEWS_LIST].length > updatedList.length
    setShowMore(showMoreBtn)
    setLoadMoreDataLoading(false)
  }

  const onLoadLess = async () => {
    const clonedList = [...list]
    const newIndex = clonedList.length - LIMIT
    setIndex(newIndex)
    setLoadLessDataLoading(true)
    await sleep(1000)
    if (clonedList.length === 5) {
      clonedList.splice(4, 1)
    } else {
      clonedList.splice(clonedList.length - LIMIT, LIMIT)
    }
    const lessButton = clonedList.length > 4 && clonedList.length > 0
    setShowLess(lessButton)
    const showMoreBtn = [...NEWS_LIST].length >= clonedList.length
    setShowMore(showMoreBtn)
    setList(clonedList)
    setLoadLessDataLoading(false)
  }

  const handleVideoClick = (videoID, url) => {
    if (videoID) {
      setCurrentVideoId(videoID)
      setVideoOpen(true)
    } else {
      if (typeof window !== 'undefined') {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
        if (newWindow) newWindow.opener = null
      }
    }
  }

  const loadMore = (
    <div
      style={{
        textAlign: 'center',
        marginTop: 12,
        height: 32,
        lineHeight: '32px'
      }}
    >
      <Button className={`${NewsMediaStyle["more-button"]}`} disabled={!showMore} loading={isLoadMoreDataLoading} onClick={onLoadMore}>
        MORE
      </Button>
      <Button
        className={`${NewsMediaStyle["more-button"]} ${NewsMediaStyle["margin-left-8"]}`}
        disabled={!showLess}
        loading={isLoadLessDataLoading}
        onClick={onLoadLess}
      >
        LESS
      </Button>
    </div>
  )

  const loadVideoMore = MORE_VIDEOS && (
    <div
      style={{
        textAlign: 'center',
        marginTop: 12,
        height: 32,
        lineHeight: '32px'
      }}
    >
      <Button className={`${NewsMediaStyle["more-button"]}`}>MORE</Button>
    </div>
  )

  const onPlay = index => {
    MEDIA_VIDEO_URLS[index].isPlaying = true
    setSelectedIndex(index)
  }

  const onPause = index => {
    setSelectedIndex(index)
  }

  const onEnded = index => {
    setSelectedIndex(index)
  }

  const carousalItems = MEDIA_VIDEO_URLS
    ? MEDIA_VIDEO_URLS.map((item, index) => (
        <>
          <div className={`${PlayerStyle['player-container']}`}>
            {(!item.isPlaying || !hasLoaded) && (
              <div className={`${PlayerStyle['thumbnail-container']}`}>
                <img
                  alt="" //{ item.title }
                  src={ item.thumbnail }
                  className={`${PlayerStyle['thumbnailImage']}`}
                  loading="lazy"
                  onClick={() => {
                        startTransition(() => {
                          onPlay(index);
                        });
                      }}
                />
                <img
                  alt="Play Video"
                  src="https://assets.cure.com/img/main-site/youtube-logo.svg"
                  className={`${PlayerStyle['playIcon']}`}
                  loading="lazy"
                  onClick={() => {
                    startTransition(() => {
                      onPlay(index);
                    });
                  }}
                />  
              </div>
            )}
            <Suspense fallback={<div></div>}>
              {
                item.isPlaying ? 
                <Player 
                  onEnded={() => onEnded(index)}
                  onPause={() => onPause(index)}
                  onPlay={() => onPlay(index)}
                  playing={item.isPlaying}
                  videoId= {`${item.videoID}`}
                  setHasLoaded= {setHasLoaded} 
                /> : 
                null
              }
            </Suspense>
          </div>
        </>
      ))
    : []

  return (
    <>
      <SEO
        description="See the latest news and media from CURE Auto Insurance."
        title="News and Media | CURE Auto Insurance"
        url="/news-media"
      />
      <HeaderHero
        desktopImage={desktopImage}
        largeScreenImage={largeScreenImage}
        mobileImage={mobileImage}
        mobileImage768={mobileImage768}
        smallImage={smallImage}
        smallImage390={smallImage390}
        tabletImage={tabletImage}
        title="NEWS/MEDIA"
        xtraLargeScreenImage={xtraLargeScreenImage}
        alt={"News & Media - CURE auto insurance"}
      />
      <div className={`${NewsMediaStyle["news-media-screen"]} page-text-margin`}>
        <Row>
          <Col className={`${NewsMediaStyle["news-container"]}`} xl={{ span: 11 }} xs={{ span: 22 }}>
            <section className={`${NewsMediaStyle["margin-bottom-8"]}`}>
              <div>
                <span className={`${NewsMediaStyle["subtitle"]}`}>ON AIR</span>
              </div>
            </section>
            <Row className={`${NewsMediaStyle["blogs-module"]}`}>
              <Col className={`${NewsMediaStyle["videoContainer"]}`} lg={{ span: 7 }} md={{ span: 7 }} sm={16} xl={{ span: 20 }} xs={22}>
                <div>
                  {MEDIA_VIDEO_URLS && (
                    <AliceCarousel
                      activeIndex={selectedIndex}
                      autoPlayDirection="ltr"
                      autoPlayInterval={2000}
                      autoPlayStrategy="all"
                      controlsStrategy="alternate"
                      disableAutoPlayOnAction={true}
                      infinite
                      items={carousalItems}
                      mouseTrackingEnabled={true}
                      responsive={RESPONSIVE_VIDEO_BREAK_POINTS}
                    />
                  )}
                </div>
              </Col>
            </Row>
            <section className={`${NewsMediaStyle["ad-archive-section"]}`}>
              <div>
                <span className={`${NewsMediaStyle["subtitle"]}`}>AD ARCHIVE</span>
              </div>
            </section>
            <Row>
              {EXTRA_VIDEO_URLS.map((video, index) => (
                <Col key={index} style={{ marginBottom: 12 }} xl={{ span: 12 }}>
                  <a onClick={() => handleVideoClick(video.videoID, video.url)}>
                    <Card
                      className={index % 2 ? `${NewsMediaStyle["video-thumbnail-even"]}` : `${NewsMediaStyle["video-thumbnail"]}`}
                      cover={
                        <>
                          <img alt={video.title} className={`${NewsMediaStyle[video.className]}`} />
                          <p>{video.title}</p>
                        </>
                      }
                      hoverable
                    />
                  </a>
                </Col>
              ))}
            </Row>
            {EXTRA_VIDEO_URLS.length > 8 && loadVideoMore}
          </Col>
          <Col className={`${NewsMediaStyle["news-container"]}`} xl={{ span: 11, offset: 1 }} xs={{ span: 22 }}>
            <section>
              <div>
                <span className={`${NewsMediaStyle["subtitle"]}`}>IN THE NEWS</span>
              </div>
            </section>
            <List
              dataSource={list}
              itemLayout="vertical"
              loadMore={loadMore}
              renderItem={item => (
                <List.Item key={item.title}>
                  <List.Item.Meta
                    description={item.description}
                    title={
                      item.isExternal ? (
                        <>
                          <a className={`${NewsMediaStyle["blog-title"]} ${NewsMediaStyle["headline"]}`} href={item.href} rel="noreferrer" target="_blank">
                            {item.title}
                          </a>
                          <a className={`${NewsMediaStyle["date-text"]}`} href={item.href} rel="noreferrer" target="_blank">
                            {item.date}
                          </a>
                        </>
                      ) : (
                        <>
                          <a className={`${NewsMediaStyle["blog-title"]}`} href={item.href}>
                            {item.title}
                          </a>
                          <a href={item.href} rel="noreferrer" target="_blank">
                            {item.date}
                          </a>
                        </>
                      )
                    }
                  />
                </List.Item>
              )}
              size="large"
            />
          </Col>
        </Row>
        {typeof window !== 'undefined' && (
          <div className={`${ModalVideoStyle['modal-video-container']}`}>
            <ModalVideo
              autoplay
              channel="youtube"
              isOpen={isVideoOpen}
              onClose={() => setVideoOpen(false)}
              videoId={currentVideoId}
            />
          </div>
          
        )}
      </div>
    </>
  )
}

NewsMedia.propTypes = {}

export default NewsMedia
